import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { isUSCountry } from '../../utils/utilities';

export enum BUSINESS_INFO_FORM_FIELDS {
	COMPANY_NAME = 'LegalEntityName',
	DESCRIPTOR = 'Descriptor',
	TAX = 'TaxId',
	MCC = 'Mcc',
	WEB_URL = 'Website',
	PLATFORM = 'EcommercePlatform',
	LINE_1 = 'Addressline1',
	LINE_2 = 'Addressline2',
	CITY = 'City',
	ZIPCODE = 'ZipCode',
	COUNTRY = 'Country',
	STATE = 'State',
	// LOGO = 'Logo',
}

export enum BUSINESS_INFO_FORM_FIELDS_NAMES {
	COMPANY_NAME = 'NEW_APPLICATION.LABEL.COMPANY_NAME',
	DESCRIPTOR = 'NEW_APPLICATION.LABEL.DESCRIPTOR',
	TAX = 'NEW_APPLICATION.LABEL.TAX',
	MCC = 'NEW_APPLICATION.LABEL.MCC',
	WEB_URL = 'NEW_APPLICATION.LABEL.WEB_URL',
	PLATFORM = 'NEW_APPLICATION.LABEL.PLATFORM',
	LINE_1 = 'NEW_APPLICATION.LABEL.LINE_1',
	LINE_2 = 'NEW_APPLICATION.LABEL.LINE_2',
	CITY = 'NEW_APPLICATION.LABEL.CITY',
	ZIPCODE = 'NEW_APPLICATION.LABEL.ZIPCODE',
	COUNTRY = 'NEW_APPLICATION.LABEL.COUNTRY',
	STATE = 'NEW_APPLICATION.LABEL.STATE',
	// LOGO = 'NEW_APPLICATION.LABEL.LOGO'
}

export type BusinessDetailsTypes = {
	[BUSINESS_INFO_FORM_FIELDS.COMPANY_NAME]: string,
	[BUSINESS_INFO_FORM_FIELDS.TAX]: string,
	[BUSINESS_INFO_FORM_FIELDS.WEB_URL]: string,
	[BUSINESS_INFO_FORM_FIELDS.MCC]: string,
	[BUSINESS_INFO_FORM_FIELDS.LINE_1]: string,
	[BUSINESS_INFO_FORM_FIELDS.LINE_2]?: string,
	[BUSINESS_INFO_FORM_FIELDS.ZIPCODE]: string,
	[BUSINESS_INFO_FORM_FIELDS.COUNTRY]: string,
	[BUSINESS_INFO_FORM_FIELDS.STATE]: string,
	[BUSINESS_INFO_FORM_FIELDS.CITY]: string,
	[BUSINESS_INFO_FORM_FIELDS.DESCRIPTOR]?: string,
	[BUSINESS_INFO_FORM_FIELDS.PLATFORM]?: string,
};

export type BusinessDetailsReturnTypes = {
	legalEntityName: string,
	companyName: string,
	taxId: string,
	descriptor: string,
	ecommercePlatform: string,
	mcc: string,
	website: string,
	address: BusinessDetailsAddressReturnTypes
};

type BusinessDetailsAddressReturnTypes = {
	line1: string,
	line2: string,
	zipCode: string,
	state: string,
	stateCode: string,
	city: string,
	country: string,
}

// @ts-ignore
export const getBusinessDetailsSchema = (t: TFunction): Yup.SchemaOf<BusinessDetailsTypes> => Yup.object().shape({
	[BUSINESS_INFO_FORM_FIELDS.COMPANY_NAME]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.COMPANY_NAME_REQUIRED'))
		.max(100, t('NEW_APPLICATION.VALIDATION.COMPANY_NAME_LENGTH')),
	[BUSINESS_INFO_FORM_FIELDS.TAX]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.TAX_REQUIRED')),
	[BUSINESS_INFO_FORM_FIELDS.WEB_URL]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.WEB_URL_REQUIRED'))
		.url(t('NEW_APPLICATION.VALIDATION.WEB_URL_FORMAT')),
	[BUSINESS_INFO_FORM_FIELDS.MCC]: Yup.string()
		.typeError(t('NEW_APPLICATION.VALIDATION.MCC_REQUIRED'))
		.required(t('NEW_APPLICATION.VALIDATION.MCC_REQUIRED')),
	[BUSINESS_INFO_FORM_FIELDS.LINE_1]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.LINE_1_REQUIRED')),
	[BUSINESS_INFO_FORM_FIELDS.CITY]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.CITY_REQUIRED')),
	[BUSINESS_INFO_FORM_FIELDS.ZIPCODE]: Yup.number()
		.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
		.required(t('NEW_APPLICATION.VALIDATION.ZIPCODE_REQUIRED')),
	[BUSINESS_INFO_FORM_FIELDS.COUNTRY]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.COUNTRY_REQUIRED')),
	[BUSINESS_INFO_FORM_FIELDS.STATE]: Yup.string()
		.typeError(t('NEW_APPLICATION.VALIDATION.STATE_REQUIRED'))
		.required(t('NEW_APPLICATION.VALIDATION.STATE_REQUIRED')),
});


export const businessInitialValues: BusinessDetailsTypes = {
	[BUSINESS_INFO_FORM_FIELDS.COMPANY_NAME]: '',
	[BUSINESS_INFO_FORM_FIELDS.DESCRIPTOR]: '',
	[BUSINESS_INFO_FORM_FIELDS.TAX]: '',
	[BUSINESS_INFO_FORM_FIELDS.MCC]: '',
	[BUSINESS_INFO_FORM_FIELDS.WEB_URL]: '',
	[BUSINESS_INFO_FORM_FIELDS.PLATFORM]: '',
	[BUSINESS_INFO_FORM_FIELDS.LINE_1]: '',
	[BUSINESS_INFO_FORM_FIELDS.LINE_2]: '',
	[BUSINESS_INFO_FORM_FIELDS.CITY]: '',
	[BUSINESS_INFO_FORM_FIELDS.ZIPCODE]: '',
	[BUSINESS_INFO_FORM_FIELDS.COUNTRY]: 'US',
	[BUSINESS_INFO_FORM_FIELDS.STATE]: '',
	// [BUSINESS_INFO_FORM_FIELDS.LOGO]: '',
};

export const getFormattedBusinessFormFields = (formikValues): BusinessDetailsReturnTypes => ({
	legalEntityName: formikValues?.[BUSINESS_INFO_FORM_FIELDS.COMPANY_NAME],
	companyName: formikValues?.[BUSINESS_INFO_FORM_FIELDS.COMPANY_NAME],
	taxId: formikValues?.[BUSINESS_INFO_FORM_FIELDS.TAX],
	descriptor: formikValues?.[BUSINESS_INFO_FORM_FIELDS.DESCRIPTOR],
	ecommercePlatform: formikValues?.[BUSINESS_INFO_FORM_FIELDS.PLATFORM],
	mcc: formikValues?.[BUSINESS_INFO_FORM_FIELDS.MCC],
	website: formikValues?.[BUSINESS_INFO_FORM_FIELDS.WEB_URL],
	address: {
		line1: formikValues?.[BUSINESS_INFO_FORM_FIELDS.LINE_1],
		line2: formikValues?.[BUSINESS_INFO_FORM_FIELDS.LINE_2],
		zipCode: formikValues?.[BUSINESS_INFO_FORM_FIELDS.ZIPCODE].toString(),
		state: isUSCountry(formikValues?.[BUSINESS_INFO_FORM_FIELDS.COUNTRY]) ? formikValues?.[BUSINESS_INFO_FORM_FIELDS.STATE] : null,
		stateCode: 'ST',
		city: formikValues?.[BUSINESS_INFO_FORM_FIELDS.CITY],
		country: formikValues?.[BUSINESS_INFO_FORM_FIELDS.COUNTRY],
	},

});

export const convertToInitialBusiness = (data): BusinessDetailsTypes => ({
	[BUSINESS_INFO_FORM_FIELDS.COMPANY_NAME]: data?.legalEntityName ? data.legalEntityName : '',
	[BUSINESS_INFO_FORM_FIELDS.DESCRIPTOR]: data?.description ? data?.description : '',
	[BUSINESS_INFO_FORM_FIELDS.TAX]: data?.taxId ? data?.taxId : '',
	[BUSINESS_INFO_FORM_FIELDS.MCC]: data?.mcc ? data?.mcc : '',
	[BUSINESS_INFO_FORM_FIELDS.WEB_URL]: data?.website? data?.website : '',
	[BUSINESS_INFO_FORM_FIELDS.PLATFORM]: data?.ecommercePlatform ? data?.ecommercePlatform : '',
	[BUSINESS_INFO_FORM_FIELDS.LINE_1]: data?.address?.line1 ? data?.address?.line1 : '',
	[BUSINESS_INFO_FORM_FIELDS.LINE_2]: data?.address?.line2 ? data?.address?.line2 : '',
	[BUSINESS_INFO_FORM_FIELDS.CITY]: data?.address?.city ? data?.address?.city : '',
	[BUSINESS_INFO_FORM_FIELDS.ZIPCODE]: data?.address?.zipCode ? data?.address?.zipCode : '',
	[BUSINESS_INFO_FORM_FIELDS.COUNTRY]: data?.address?.country ? data?.address?.country : 'US',
	[BUSINESS_INFO_FORM_FIELDS.STATE]: data?.address?.state ? data?.address?.state : '',

});
